import { useQuery } from "@tanstack/react-query";
import { useEmailStore } from "../store/emailStore";
import { AppLayout } from "../components/AppLayout";
import { MailWorkspace } from "../components/MailWorkspace";
import { ThreadDetail } from "../components/ThreadDetail";
import { queueApi, QueueItem } from "../api/queue";
import { casesApi, CaseItem } from "../api/cases";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface TriagePageProps {
  onNotificationClick?: () => void;
}

export function TriagePage({ onNotificationClick }: TriagePageProps) {
  const { selectedFilter, setSelectedFilter, selectedVetId } = useEmailStore();
  const {
    data: emails = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery<QueueItem[]>({
    queryKey: ["mail-queue", selectedVetId],
    refetchInterval: 30000,
    queryFn: async () => {
      if (selectedVetId) {
        const cases = await casesApi.list({
          vet_id: selectedVetId,
          status: "open",
        });
        return cases.map((c: CaseItem) => ({
          case_id: c.id,
          patient_name: null,
          patient_species: null,
          owner_email: null,
          case_title: c.title || "",
          specialty: c.specialty,
          priority: c.priority || "low",
          status: c.status,
          routing_conflict: false,
          vet_id: c.vet_id,
          vet_name: c.vet_name,
          email_count: 0,
          latest_subject: null,
          latest_received_at: null,
          latest_email_id: null,
          summary_ai: null,
        }));
      }
      const [unassigned, conflicts] = await Promise.all([
        queueApi.getUnassigned(),
        queueApi.getConflicts(),
      ]);
      return Array.from(
        new Map(
          [...unassigned, ...conflicts].map((item) => [item.case_id, item]),
        ).values(),
      );
    },
    retry: false,
  });

  const filteredEmails = emails.filter((email) => {
    if (selectedFilter === "all") return true;
    if (selectedFilter === "conflicts") return email.routing_conflict;
    if (selectedFilter === "urgent")
      return email.priority === "urgent" || email.priority === "high";
    if (selectedFilter === "appointments")
      return email.case_title?.toLowerCase().includes("appuntamento");
    if (selectedFilter === "clinics") return email.specialty != null;
    if (selectedFilter === "unassigned") return !email.vet_id;
    return true;
  });

  const counts = {
    all: emails.length,
    urgent: emails.filter(
      (e) => e.priority === "urgent" || e.priority === "high",
    ).length,
    appointments: emails.filter(
      (e) => e.case_title?.toLowerCase().includes("appuntamento") || false,
    ).length,
    clinics: emails.filter((e) => e.specialty != null).length,
    unassigned: emails.filter((e) => !e.vet_id).length,
    conflicts: emails.filter((e) => e.routing_conflict).length,
  };

  return (
    <AppLayout
      breadcrumbs={[{ label: "Triage" }]}
      onNotificationClick={onNotificationClick}
      mainClassName="mail-main"
      sidebarProps={{
        selectedFilter,
        onFilterChange: setSelectedFilter,
        counts,
        showFilters: true,
      }}
      topbarActions={
        error && (
          <Button
            onClick={() => {
              void refetch();
            }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="size-3.5" />
            Riprova
          </Button>
        )
      }
    >
      <MailWorkspace
        title="Conversazioni"
        loading={loading}
        error={!!error}
        onRetry={() => {
          void refetch();
        }}
        empty="Nessun caso da mostrare"
        selectionKey="case"
        items={filteredEmails.map((item) => ({
          id: item.case_id,
          title: item.case_title || item.latest_subject || "Senza titolo",
          sender:
            item.patient_name ||
            item.owner_email ||
            item.vet_name ||
            "Caso clinico",
          preview: item.summary_ai || item.latest_subject,
          date: item.latest_received_at,
          priority: item.priority,
          meta: [
            item.routing_conflict
              ? "Conflitto di assegnazione"
              : item.vet_name || "Da assegnare",
            item.specialty,
            item.email_count ? `${item.email_count} email` : null,
          ]
            .filter(Boolean)
            .join(" · "),
        }))}
        renderDetail={(id) => <ThreadDetail caseId={id} embedded />}
      />
    </AppLayout>
  );
}
